import { useState } from 'react';
import { motion } from 'framer-motion';
import { PageHero, SectionHeader } from '@/components/common/Sections';
import { CTABanner } from '@/components/common/Cards';
import { CAREER_POSITIONS, IMAGES } from '@/lib/constants';
import { MapPin, Clock, Briefcase, ArrowRight, Users, Rocket, Heart } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { submitCareerApplication } from '@/lib/api';
import { toast } from 'sonner';

export default function CareersPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', position: '', experience: '', cover_letter: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitCareerApplication(formData);
      toast.success('Application submitted successfully! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', position: '', experience: '', cover_letter: '' });
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Users, title: "Collaborative Culture", description: "Work with passionate travel professionals" },
    { icon: Rocket, title: "Growth Opportunities", description: "Clear career progression paths" },
    { icon: Heart, title: "Great Benefits", description: "Health insurance, travel perks & more" },
  ];

  return (
    <div className="page-transition">
      <PageHero title="Careers at Osaka" subtitle="Join our team of travel enthusiasts" backgroundImage={IMAGES.corporateTravel} />

      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader overline="Life at Osaka" title="Why Work With Us" description="At Osaka Group, we believe in fostering talent and creating an environment where everyone can thrive." />
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center p-8 bg-[#F5F5F5]">
                <benefit.icon size={40} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-lg font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-2">{benefit.title}</h3>
                <p className="text-[#4A5568] text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="open-positions" className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <SectionHeader overline="Join Us" title="Open Positions" />
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {CAREER_POSITIONS.map((position, index) => (
              <motion.div key={position.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} data-testid={`position-card-${position.id}`} className="bg-white border border-slate-200 p-6 card-hover">
                <h3 className="text-xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-4">{position.title}</h3>
                <div className="space-y-2 text-sm text-[#4A5568] mb-4">
                  <div className="flex items-center gap-2"><MapPin size={16} className="text-[#D4AF37]" />{position.location}</div>
                  <div className="flex items-center gap-2"><Clock size={16} className="text-[#D4AF37]" />{position.type}</div>
                  <div className="flex items-center gap-2"><Briefcase size={16} className="text-[#D4AF37]" />{position.experience}</div>
                </div>
                <a href="#apply" className="inline-flex items-center gap-2 text-[#D4AF37] font-medium">Apply Now <ArrowRight size={16} /></a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" data-testid="career-application" className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <SectionHeader overline="Apply Now" title="Submit Your Application" description="Fill out the form below and our HR team will review your application." />
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Full Name *</label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="career-name-input" required className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Email *</label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="career-email-input" required className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Phone *</label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="career-phone-input" required className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Position *</label>
                  <Select value={formData.position} onValueChange={(value) => setFormData({...formData, position: value})}>
                    <SelectTrigger data-testid="career-position-select" className="rounded-none border-slate-300"><SelectValue placeholder="Select position" /></SelectTrigger>
                    <SelectContent>{CAREER_POSITIONS.map((p) => (<SelectItem key={p.id} value={p.title}>{p.title}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Years of Experience *</label>
                <Input value={formData.experience} onChange={(e) => setFormData({...formData, experience: e.target.value})} data-testid="career-experience-input" required placeholder="e.g., 3 years" className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Cover Letter</label>
                <Textarea value={formData.cover_letter} onChange={(e) => setFormData({...formData, cover_letter: e.target.value})} data-testid="career-cover-input" rows={5} placeholder="Tell us why you'd be a great fit..." className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
              </div>
              <Button type="submit" data-testid="career-submit-btn" disabled={loading} className="w-full bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] py-6 rounded-none font-semibold">
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
